import React from "react";
import {
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Collapse,
    Typography,
    Tooltip,
    Button,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import {ExpandMore, ExpandLess} from "@mui/icons-material";
import {Link, useLocation} from "react-router-dom";
import QueryStatsIcon from "@mui/icons-material/QueryStats";

const getStatusColor = (status) => {
    if (status.startsWith("RUNNING")) return "green";
    if (status.startsWith("FAILED")) return "red";
    return "#ffc107";
};

function RunStatusView({
                           t,
                           runsOpen,
                           groupedRuns,
                           scrollableHeight,
                           showFailedRuns,
                           setShowFailedRuns,
                           openDatasets,
                           openExperiments,
                           handleToggleDataset,
                           handleToggleExperiment,
                           matches,
                           handleToggleDrawer,
                       }) {
    const location = useLocation();

    return (
        <Collapse
            in={runsOpen}
            timeout="auto"
            unmountOnExit
            sx={{
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                flex: 1,
                maxHeight: "500px",
                backgroundColor: runsOpen ? "rgba(0, 0, 0, 0.05)" : "transparent",
                transition: "background-color 300ms ease-in-out",
                borderRadius: 3,
            }}
        >
            <Box
                sx={{
                    overflow: "auto",
                    maxHeight: scrollableHeight,
                    paddingRight: "4px",
                }}
            >
                <Box
                    sx={{
                        height: "100%",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <List
                        component="div"
                        disablePadding
                        sx={{flex: 1, overflow: "hidden"}}
                    >
                        {Object.keys(groupedRuns).length === 0 ? (
                            <ListItem sx={{pl: 4}}>
                                <ListItemText primary={t("sidebar.NoRunsInfo")}/>
                            </ListItem>
                        ) : (
                            Object.entries(groupedRuns).map(([datasetName, runs]) => {
                                const runsByExperiment = runs.reduce((acc, run) => {
                                    if (!acc[run.experimentName]) acc[run.experimentName] = [];
                                    acc[run.experimentName].push(run);
                                    return acc;
                                }, {});

                                const filteredExperiments = Object.entries(
                                    runsByExperiment
                                ).reduce((acc, [experimentName, experimentRuns]) => {
                                    const visibleRuns = experimentRuns.filter(
                                        (run) => showFailedRuns || !run.status.startsWith("FAILED")
                                    );

                                    if (visibleRuns.length > 0) {
                                        acc[experimentName] = visibleRuns;
                                    }
                                    return acc;
                                }, {});

                                if (Object.keys(filteredExperiments).length === 0) return null;

                                return (
                                    <Box key={datasetName}>
                                        <ListItemButton
                                            onClick={() => handleToggleDataset(datasetName)}
                                            sx={{
                                                mb: 1,
                                                ml: 1,
                                                pl: 4,
                                                backgroundColor: openDatasets[datasetName]
                                                    ? "rgba(0, 0, 0, 0.05)"
                                                    : "transparent",
                                                transition: "background-color 0.3s ease",
                                                "&:hover": {
                                                    backgroundColor: "#BAD7E9",
                                                    borderLeft: "3px solid #2879A8",
                                                },
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                width="100%"
                                            >
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Tooltip title={datasetName} arrow>
                                                        <Box
                                                            sx={{
                                                                maxWidth: "200px",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            <ListItemText
                                                                noWrap
                                                                primary={datasetName}
                                                                primaryTypographyProps={{
                                                                    sx: {
                                                                        fontWeight: "bold",
                                                                        maxWidth: "100%",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                    },
                                                                }}
                                                            >
                                                                {datasetName}
                                                            </ListItemText>
                                                        </Box>
                                                    </Tooltip>
                                                    <Box
                                                        sx={{
                                                            backgroundColor: "#2879A8",
                                                            color: "white",
                                                            fontSize: "bold",
                                                            minWidth: 20,
                                                            height: 20,
                                                            px: 1,
                                                            borderRadius: "50%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        {Object.keys(filteredExperiments).length}
                                                    </Box>
                                                </Box>
                                                {openDatasets[datasetName] ? (
                                                    <ExpandLess/>
                                                ) : (
                                                    <ExpandMore/>
                                                )}
                                            </Box>
                                        </ListItemButton>

                                        <Collapse
                                            in={openDatasets?.[datasetName]}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <List component="div" disablePadding>
                                                {Object.entries(filteredExperiments).map(
                                                    ([experimentName, experimentRuns]) => (
                                                        <Box key={experimentName}>
                                                            <ListItemButton
                                                                onClick={() =>
                                                                    handleToggleExperiment(
                                                                        datasetName,
                                                                        experimentName
                                                                    )
                                                                }
                                                                sx={{
                                                                    ml: 5,
                                                                    backgroundColor: openExperiments[
                                                                        datasetName
                                                                        ]?.[experimentName]
                                                                        ? "rgba(0, 0, 0, 0.05)"
                                                                        : "transparent",
                                                                    borderLeft: openExperiments[datasetName]?.[
                                                                        experimentName
                                                                        ]
                                                                        ? "1px solid #2879A8"
                                                                        : "1px solid transparent",
                                                                    transition:
                                                                        "background-color 0.3s ease, border-left-color 0.3s ease",
                                                                    "&:hover": {
                                                                        backgroundColor: "#BAD7E9",
                                                                        borderLeftColor: "#BAD7E9",
                                                                    },
                                                                }}
                                                            >
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    justifyContent="space-between"
                                                                    width="100%"
                                                                >
                                                                    <Tooltip title={experimentName} arrow>
                                                                        <ListItemText
                                                                            noWrap
                                                                            primary={experimentName}
                                                                            primaryTypographyProps={{
                                                                                sx: {
                                                                                    fontWeight: "bold",
                                                                                    maxWidth: "100%",
                                                                                    whiteSpace: "nowrap",
                                                                                    overflow: "hidden",
                                                                                    textOverflow: "ellipsis",
                                                                                },
                                                                            }}
                                                                        >
                                                                            {experimentName}
                                                                        </ListItemText>
                                                                    </Tooltip>
                                                                    <Box
                                                                        sx={{
                                                                            backgroundColor: "#2879A8",
                                                                            color: "white",
                                                                            fontWeight: "bold",
                                                                            minWidth: 20,
                                                                            height: 20,
                                                                            px: 1,
                                                                            borderRadius: "50%",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            ml: 1,
                                                                        }}
                                                                    >
                                                                        {experimentRuns.length}
                                                                    </Box>
                                                                    {openExperiments[datasetName]?.[
                                                                        experimentName
                                                                        ] ? (
                                                                        <ExpandLess/>
                                                                    ) : (
                                                                        <ExpandMore/>
                                                                    )}
                                                                </Box>
                                                            </ListItemButton>

                                                            <Collapse
                                                                in={
                                                                    openExperiments?.[datasetName]?.[
                                                                        experimentName
                                                                        ]
                                                                }
                                                                timeout="auto"
                                                                unmountOnExit
                                                            >
                                                                <List component="div" disablePadding>
                                                                    {(() => {
                                                                        if (experimentRuns.length === 0) {
                                                                            return (
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        pl: 5,
                                                                                        pr: 2,
                                                                                        color: "gray",
                                                                                        fontStyle: "italic",
                                                                                        whiteSpace: "normal",
                                                                                        wordBreak: "break-word",
                                                                                        maxWidth: "calc(100% - 40px)",
                                                                                    }}
                                                                                >
                                                                                    {t("sidebar.CheckboxMessage")}
                                                                                </Typography>
                                                                            );
                                                                        }
                                                                        return experimentRuns.map((run) => {
                                                                            const runPath = `/train/${run.datasetName}/${run.experimentName}/pipeline/${run.runName}`;
                                                                            const isActive =
                                                                                location.pathname === runPath;
                                                                            return (
                                                                                <ListItem
                                                                                    key={run.runName}
                                                                                    sx={{
                                                                                        pl: 5,
                                                                                        "&:hover": {
                                                                                            backgroundColor: "transparent",
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    <Button
                                                                                        component={Link}
                                                                                        to={runPath}
                                                                                        onClick={() => {
                                                                                            if (!matches)
                                                                                                handleToggleDrawer();
                                                                                        }}
                                                                                        size="small"
                                                                                        startIcon={
                                                                                            <QueryStatsIcon
                                                                                                fontSize="small"/>
                                                                                        }
                                                                                        sx={{
                                                                                            justifyContent: "flex-start",
                                                                                            textTransform: "none",
                                                                                            width: "100%",
                                                                                            borderRadius: "8px",
                                                                                            backgroundColor: isActive
                                                                                                ? "#709acb"
                                                                                                : "transparent",
                                                                                            fontWeight: isActive
                                                                                                ? "bold"
                                                                                                : "normal",
                                                                                            color: isActive
                                                                                                ? "#0d47a1"
                                                                                                : "inherit",
                                                                                            overflow: "hidden",
                                                                                            "&:hover": {
                                                                                                backgroundColor: isActive
                                                                                                    ? "#709acb"
                                                                                                    : "#BAD7E9",
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <Box
                                                                                            display="flex"
                                                                                            alignItems="center"
                                                                                            gap={1}
                                                                                            width="100%"
                                                                                            overflow="hidden"
                                                                                        >
                                                                                            <Typography
                                                                                                noWrap
                                                                                                primaryTypographyProps={{
                                                                                                    sx: {
                                                                                                        maxWidth: "100%",
                                                                                                        whiteSpace: "nowrap",
                                                                                                        overflow: "hidden",
                                                                                                        textOverflow: "ellipsis",
                                                                                                    },
                                                                                                }}
                                                                                            >
                                                                                                {run.runName}
                                                                                            </Typography>
                                                                                            <Tooltip title={run.status}
                                                                                                     arrow>
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        minWidth: 10,
                                                                                                        minHeight: 10,
                                                                                                        width: 10,
                                                                                                        height: 10,
                                                                                                        flexShrink: 0,
                                                                                                        borderRadius: "50%",
                                                                                                        backgroundColor:
                                                                                                            getStatusColor(
                                                                                                                run.status
                                                                                                            ),

                                                                                                        animation:
                                                                                                            run.status.startsWith(
                                                                                                                "RUNNING"
                                                                                                            )
                                                                                                                ? "blinker 1.2s linear infinite"
                                                                                                                : "none",

                                                                                                        "@keyframes blinker": {
                                                                                                            "50%": {
                                                                                                                opacity: 0.3,
                                                                                                            },
                                                                                                        },
                                                                                                    }}
                                                                                                />
                                                                                            </Tooltip>
                                                                                        </Box>
                                                                                    </Button>
                                                                                </ListItem>
                                                                            );
                                                                        });
                                                                    })()}
                                                                </List>
                                                            </Collapse>
                                                        </Box>
                                                    )
                                                )}
                                            </List>
                                        </Collapse>
                                    </Box>
                                );
                            })
                        )}
                    </List>
                    {Object.keys(groupedRuns).length !== 0 ? (
                        <Box
                            sx={{
                                backgroundColor: "#f9f9f9",
                                borderRadius: 2,
                                ml: 2,
                                px: 1,
                                py: 0.5,
                                mb: 4,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showFailedRuns}
                                        onChange={(e) => setShowFailedRuns(e.target.checked)}
                                        size="small"
                                        sx={{
                                            color: "#2879A8",
                                            "&.Mui-checked": {color: "#2879A8"},
                                        }}
                                    />
                                }
                                label={t("sidebar.ShowFailedRunCheckBox")}
                            />
                        </Box>
                    ) : null}
                </Box>
            </Box>
        </Collapse>
    );
}

export default RunStatusView;
